// Submenú "Caja Chica" del módulo Control Bancario.
//   Fondos · Vales y gastos · Reposición · Arqueo
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Calculator, Loader2, PiggyBank, Plus, ReceiptText, RefreshCw, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  CajaChica, CajaEstado, CajaVale, Campo, CuentaMini, GASTO_CAT, Modal, cb, cbRpc, fieldCls, hoyISO, label, money,
} from './shared';

function useCuentas() {
  return useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
}
function useCajas() {
  return useQuery<CajaChica[]>({ queryKey: ['cb-cajas'], queryFn: () => cbRpc<CajaChica[]>('cb_cajas') });
}
function useEstado(cajaId: string) {
  return useQuery<CajaEstado>({
    queryKey: ['cb-caja-estado', cajaId],
    queryFn: () => cbRpc<CajaEstado>('cb_caja_estado', { _caja_id: cajaId }),
    enabled: !!cajaId,
  });
}
const invalidar = (qc: ReturnType<typeof useQueryClient>) =>
  ['cb-cajas', 'cb-caja-estado', 'cb-caja-vales', 'cb-caja-repos', 'cb-caja-arqueos', 'cb-libro', 'cb-posicion']
    .forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));

function SelectCaja({ cajas, value, onChange }: { cajas: CajaChica[]; value: string; onChange: (v: string) => void }) {
  return (
    <select className={fieldCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {cajas.length === 0 && <option value="">Sin cajas</option>}
      {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}
    </select>
  );
}

// ============================================================ FONDOS ==========
const abrirVacio = { nombre: '', responsable: '', monto: 0, cuenta_id: '', fecha: hoyISO() };

export function CajaFondos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useCuentas();
  const { data: cajas = [], isFetching } = useCajas();
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof abrirVacio>(abrirVacio);
  const set = (patch: Partial<typeof abrirVacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = () => { setForm({ ...abrirVacio, cuenta_id: cuentas[0]?.id ?? '' }); setAbierto(true); };
  const crear = useMutation({
    mutationFn: () => cbRpc('cb_caja_abrir', { _nombre: form.nombre, _responsable: form.responsable || null, _monto: Number(form.monto), _cuenta_id: form.cuenta_id, _fecha: form.fecha }),
    onSuccess: () => { toast.success('Caja abierta'); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('cajas_chicas', id),
    onSuccess: () => { toast.success('Caja eliminada'); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><PiggyBank className="w-5 h-5" /> Fondos de caja chica</h1>
        <Button onClick={abrir} disabled={cuentas.length === 0}><Plus className="w-4 h-4 mr-1" /> Abrir fondo</Button>
      </div>
      {isFetching && <div className="py-2"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {cajas.length === 0 && !isFetching && <p className="text-sm text-muted-foreground">Aún no hay cajas chicas.</p>}
        {cajas.map((c) => (
          <Card key={c.id}>
            <CardHeader className="pb-2 flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">{c.nombre}</CardTitle>
                <p className="text-xs text-muted-foreground">{c.responsable ?? 'Sin responsable'} · {c.cuenta}</p>
              </div>
              <button className="rounded-md p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Ban className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Monto asignado</span><span className="font-mono">{money(c.monto_asignado, c.moneda)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Efectivo disponible</span><span className="font-mono font-semibold text-estable">{money(c.saldo_efectivo, c.moneda)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Vales pendientes</span><span className="font-mono">{c.n_pendientes} · {money(c.total_pendiente, c.moneda)}</span></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {abierto && (
        <Modal title="Abrir fondo de caja chica" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="p.ej. Caja Oficina Matriz" /></Campo>
            <Campo label="Responsable (custodio)"><input className={fieldCls} value={form.responsable} onChange={(e) => set({ responsable: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Monto asignado *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
            </div>
            <Campo label="Cuenta de fondeo *">
              <select className={fieldCls} value={form.cuenta_id} onChange={(e) => set({ cuenta_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
              </select>
            </Campo>
            <p className="text-xs text-muted-foreground">Se registrará un débito en la cuenta seleccionada por el monto asignado.</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={crear.isPending} disabled={!form.nombre.trim() || !form.cuenta_id || Number(form.monto) <= 0} onClick={() => crear.mutate()}>Abrir</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================ VALES ===========
const valeVacio = { fecha: hoyISO(), monto: 0, categoria: '', beneficiario: '', concepto: '', comprobante: '' };

export function CajaVales() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cajas = [] } = useCajas();
  const [cajaId, setCajaId] = useState('');
  const caja = cajaId || cajas[0]?.id || '';
  const { data: est } = useEstado(caja);
  const moneda = est?.caja.moneda ?? 'USD';
  const { data: historial = [], isFetching } = useQuery<CajaVale[]>({
    queryKey: ['cb-caja-vales', caja],
    queryFn: () => cb.list<CajaVale>('caja_vales', `?select=*&caja_id=eq.${caja}&deleted_at=is.null&order=fecha.desc,created_at.desc&limit=100`),
    enabled: !!caja,
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof valeVacio>(valeVacio);
  const set = (patch: Partial<typeof valeVacio>) => setForm((f) => ({ ...f, ...patch }));

  const registrar = useMutation({
    mutationFn: () => cbRpc('cb_caja_vale', { _caja_id: caja, _fecha: form.fecha, _monto: Number(form.monto), _categoria: form.categoria || null, _beneficiario: form.beneficiario || null, _concepto: form.concepto || null, _comprobante: form.comprobante || null }),
    onSuccess: () => { toast.success('Vale registrado'); setAbierto(false); setForm(valeVacio); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => cb.update('caja_vales', id, { estado: 'anulado' }),
    onSuccess: () => { toast.success('Vale anulado'); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ReceiptText className="w-5 h-5" /> Vales y gastos</h1>
        <Button onClick={() => { setForm(valeVacio); setAbierto(true); }} disabled={!caja}><Plus className="w-4 h-4 mr-1" /> Registrar vale</Button>
      </div>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-4">
        <Campo label="Caja" className="min-w-[200px]"><SelectCaja cajas={cajas} value={caja} onChange={setCajaId} /></Campo>
        <div><div className="text-xs text-muted-foreground">Efectivo disponible</div><div className="text-lg font-semibold font-mono text-estable">{money(est?.saldo_efectivo, moneda)}</div></div>
        <div><div className="text-xs text-muted-foreground">Pendiente de reposición</div><div className="text-lg font-semibold font-mono">{money(est?.total_pendiente, moneda)}</div></div>
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Vales ({historial.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3">Comprob.</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {historial.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin vales</td></tr>}
              {historial.map((v) => (
                <tr key={v.id} className={`border-b last:border-0 hover:bg-muted/40 ${v.estado === 'anulado' ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{v.fecha}</td>
                  <td className="py-2 pr-3">{label(GASTO_CAT, v.categoria)}</td>
                  <td className="py-2 pr-3">{v.beneficiario ?? '—'}</td>
                  <td className="py-2 pr-3">{v.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{v.comprobante ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(v.monto, moneda)}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${v.estado === 'pendiente' ? 'bg-pendiente-soft text-pendiente' : v.estado === 'repuesto' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>{v.estado}</span></td>
                  <td className="py-2 text-right">
                    {v.estado === 'pendiente' && <button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={async () => { if (await dlg.confirmar('¿Anular este vale?', { peligro: true })) anular.mutate(v.id); }}>Anular</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar vale" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label="Monto *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Categoría">
              <select className={fieldCls} value={form.categoria} onChange={(e) => set({ categoria: e.target.value })}>
                <option value="">— Sin categoría —</option>
                {GASTO_CAT.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Beneficiario"><input className={fieldCls} value={form.beneficiario} onChange={(e) => set({ beneficiario: e.target.value })} /></Campo>
              <Campo label="Comprobante"><input className={fieldCls} value={form.comprobante} onChange={(e) => set({ comprobante: e.target.value })} placeholder="N° recibo/factura" /></Campo>
            </div>
            <Campo label="Concepto"><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
            {est && <p className="text-xs text-muted-foreground">Disponible en caja: <span className="font-mono">{money(est.saldo_efectivo, moneda)}</span></p>}
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={Number(form.monto) <= 0} onClick={() => registrar.mutate()}>Registrar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================ REPOSICIÓN =======
interface Reposicion { id: string; fecha: string; monto_total: number; n_vales: number; notas: string | null; created_at: string; }
export function CajaReposicion() {
  const qc = useQueryClient();
  const { data: cajas = [] } = useCajas();
  const [cajaId, setCajaId] = useState('');
  const caja = cajaId || cajas[0]?.id || '';
  const { data: est } = useEstado(caja);
  const moneda = est?.caja.moneda ?? 'USD';
  const { data: repos = [], isFetching } = useQuery<Reposicion[]>({
    queryKey: ['cb-caja-repos', caja],
    queryFn: () => cb.list<Reposicion>('caja_reposiciones', `?select=*&caja_id=eq.${caja}&order=fecha.desc,created_at.desc&limit=50`),
    enabled: !!caja,
  });
  const [confirm, setConfirm] = useState(false);
  const [notas, setNotas] = useState('');

  const reponer = useMutation({
    mutationFn: () => cbRpc('cb_caja_reponer', { _caja_id: caja, _fecha: hoyISO(), _cuenta_id: null, _notas: notas || null }),
    onSuccess: (r: any) => { toast.success(`Repuesto ${money(r.monto_total, moneda)} (${r.n_vales} vales)`); setConfirm(false); setNotas(''); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Reposición de fondo</h1>
        <Button onClick={() => { setNotas(''); setConfirm(true); }} disabled={!caja || !est || est.total_pendiente <= 0}><RefreshCw className="w-4 h-4 mr-1" /> Reponer</Button>
      </div>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-4">
        <Campo label="Caja" className="min-w-[200px]"><SelectCaja cajas={cajas} value={caja} onChange={setCajaId} /></Campo>
        <div><div className="text-xs text-muted-foreground">A reponer (vales pendientes)</div><div className="text-lg font-semibold font-mono text-pendiente">{money(est?.total_pendiente, moneda)}</div></div>
        <div><div className="text-xs text-muted-foreground">Efectivo actual</div><div className="text-lg font-semibold font-mono">{money(est?.saldo_efectivo, moneda)}</div></div>
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Vales por reponer ({est?.vales_pendientes.length ?? 0})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3 text-right">Monto</th></tr></thead>
            <tbody>
              {(est?.vales_pendientes ?? []).length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin vales pendientes</td></tr>}
              {(est?.vales_pendientes ?? []).map((v) => (
                <tr key={v.id} className="border-b last:border-0"><td className="py-2 pr-3 whitespace-nowrap">{v.fecha}</td><td className="py-2 pr-3">{label(GASTO_CAT, v.categoria)}</td><td className="py-2 pr-3">{v.concepto ?? '—'}</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(v.monto, moneda)}</td></tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Reposiciones ({repos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3 text-right">Vales</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Notas</th></tr></thead>
            <tbody>
              {repos.length === 0 && !isFetching && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sin reposiciones</td></tr>}
              {repos.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40"><td className="py-2 pr-3 whitespace-nowrap">{r.fecha}</td><td className="py-2 pr-3 text-right font-mono">{r.n_vales}</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.monto_total, moneda)}</td><td className="py-2 pr-3 text-muted-foreground">{r.notas ?? '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {confirm && (
        <Modal title="Reponer caja chica" onClose={() => setConfirm(false)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Se registrará un débito de <span className="font-mono font-semibold">{money(est?.total_pendiente, moneda)}</span> en la cuenta de fondeo y la caja volverá a su monto asignado.</p>
            <Campo label="Notas"><textarea className={`${fieldCls} h-16`} value={notas} onChange={(e) => setNotas(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setConfirm(false)}>Cancelar</Button>
              <Button isLoading={reponer.isPending} onClick={() => reponer.mutate()}>Confirmar reposición</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================ ARQUEO ==========
interface Arqueo { id: string; fecha: string; saldo_teorico: number; efectivo_contado: number; diferencia: number; notas: string | null; }
export function CajaArqueo() {
  const qc = useQueryClient();
  const { data: cajas = [] } = useCajas();
  const [cajaId, setCajaId] = useState('');
  const caja = cajaId || cajas[0]?.id || '';
  const { data: est } = useEstado(caja);
  const moneda = est?.caja.moneda ?? 'USD';
  const { data: arqueos = [], isFetching } = useQuery<Arqueo[]>({
    queryKey: ['cb-caja-arqueos', caja],
    queryFn: () => cb.list<Arqueo>('caja_arqueos', `?select=*&caja_id=eq.${caja}&order=fecha.desc,created_at.desc&limit=50`),
    enabled: !!caja,
  });
  const [contado, setContado] = useState('');
  const [notas, setNotas] = useState('');
  const teorico = est?.saldo_efectivo ?? 0;
  const dif = (Number(contado) || 0) - teorico;

  const arquear = useMutation({
    mutationFn: () => cbRpc('cb_caja_arqueo', { _caja_id: caja, _fecha: hoyISO(), _efectivo_contado: Number(contado), _notas: notas || null }),
    onSuccess: (r: any) => { toast.success(`Arqueo guardado · diferencia ${money(r.diferencia, moneda)}`); setContado(''); setNotas(''); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Calculator className="w-5 h-5" /> Arqueo de caja chica</h1>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-4">
        <Campo label="Caja" className="min-w-[200px]"><SelectCaja cajas={cajas} value={caja} onChange={setCajaId} /></Campo>
        <div><div className="text-xs text-muted-foreground">Saldo teórico</div><div className="text-lg font-semibold font-mono">{money(teorico, moneda)}</div></div>
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wallet className="w-4 h-4" /> Nuevo arqueo</CardTitle></CardHeader>
        <CardContent className="space-y-3 max-w-md">
          <Campo label={`Efectivo contado (${moneda}) *`}><input type="number" step="0.01" min="0" className={fieldCls} value={contado} onChange={(e) => setContado(e.target.value)} /></Campo>
          <div className="rounded-md border p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Saldo teórico</span><span className="font-mono">{money(teorico, moneda)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Efectivo contado</span><span className="font-mono">{money(Number(contado) || 0, moneda)}</span></div>
            <div className="flex justify-between border-t pt-1 font-semibold"><span>Diferencia</span><span className={`font-mono ${Math.abs(dif) > 0.005 ? 'text-pendiente' : 'text-estable'}`}>{money(dif, moneda)} {dif < 0 ? '(faltante)' : dif > 0 ? '(sobrante)' : ''}</span></div>
          </div>
          <Campo label="Notas"><textarea className={`${fieldCls} h-16`} value={notas} onChange={(e) => setNotas(e.target.value)} /></Campo>
          <div className="flex justify-end">
            <Button isLoading={arquear.isPending} disabled={!caja || contado === ''} onClick={() => arquear.mutate()}>Guardar arqueo</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Arqueos ({arqueos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3 text-right">Teórico</th><th className="py-2 pr-3 text-right">Contado</th><th className="py-2 pr-3 text-right">Diferencia</th><th className="py-2 pr-3">Notas</th></tr></thead>
            <tbody>
              {arqueos.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin arqueos</td></tr>}
              {arqueos.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{a.fecha}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.saldo_teorico, moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.efectivo_contado, moneda)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Math.abs(a.diferencia) > 0.005 ? 'text-pendiente' : 'text-estable'}`}>{money(a.diferencia, moneda)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.notas ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
